import React, { useState, useEffect } from "react";
import { X, Copy, CheckCircle } from "lucide-react";
import { Button } from "../../../components/ui";
import { toast } from "sonner";

export default function UserModal({ 
  open, 
  onClose, 
  user, 
  createdUserWithPassword,
  onSave, 
  submitting 
}) {
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    role: "leadmaker",
    status: "active"
  });
  const [errors, setErrors] = useState({});
  const [showAdminWarning, setShowAdminWarning] = useState(false);

  useEffect(() => {
    if (user) {
      // Editing existing user
      setFormData({
        name: user.name || "",
        email: user.email || "",
        role: user.role || "leadmaker",
        status: user.status || "active"
      });
    } else {
      // Creating new user
      setFormData({
        name: "",
        email: "",
        role: "leadmaker",
        status: "active"
      });
    }
    setErrors({});
    setCopied(false);
  }, [user, open]);

  const handleCopyPassword = () => {
    if (createdUserWithPassword?.temporary_password) {
      navigator.clipboard.writeText(createdUserWithPassword.temporary_password);
      setCopied(true);
      toast.success("Password copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRoleChange = (e) => {
    const role = e.target.value;
    setFormData(prev => ({ ...prev, role }));
    setShowAdminWarning(role === 'admin');
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.length < 2 || formData.name.length > 100) {
      newErrors.name = "Name must be between 2 and 100 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {user ? "User Details" : "Create New User"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {createdUserWithPassword?.temporary_password && (
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-yellow-900 mb-2">
                      ⚠️ Important: Temporary Password
                    </h3>
                    <p className="text-xs text-yellow-800 mb-3">
                      Share this password with the user. It will only be shown once. The user must change it on first login.
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border border-yellow-300 text-sm font-mono text-gray-900">
                        {createdUserWithPassword.temporary_password}
                      </code>
                      <button
                        type="button"
                        onClick={handleCopyPassword}
                        className="px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded transition-colors flex items-center gap-2"
                      >
                        {copied ? (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm">Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4" />
                            <span className="text-sm">Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!user && !createdUserWithPassword && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter full name"
                  />
                  {errors.name && (
                    <div className="text-red-600 text-sm mt-1">{errors.name}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter email"
                  />
                  {errors.email && (
                    <div className="text-red-600 text-sm mt-1">{errors.email}</div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={formData.role}
                    onChange={handleRoleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="leadmaker">Lead Maker</option>
                    <option value="admin">Admin</option>
                  </select>
                  {errors.role && (
                    <div className="text-red-600 text-sm mt-1">{errors.role}</div>
                  )}

                  {showAdminWarning && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                      <div className="text-yellow-800 text-sm font-medium">
                        ⚠️ Warning: Admin users have full system access
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {user && (
              <>
                <div className="bg-gray-50 p-4 rounded-md space-y-3">
                  <div>
                    <span className="font-medium text-gray-700">Name:</span>
                    <div className="mt-1 text-gray-900">{user.name}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Email:</span>
                    <div className="mt-1 text-gray-900">{user.email}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Role:</span>
                    <div className="mt-1 text-gray-900 capitalize">{user.role}</div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Status:</span>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                      className="mt-1 block px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="active">Active</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Created:</span>
                    <div className="mt-1 text-gray-900">
                      {new Date(user.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              {createdUserWithPassword ? "Done" : user ? "Close" : "Cancel"}
            </Button>
            {!createdUserWithPassword && (
              <Button
                type="submit"
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? "Saving..." : user ? "Update" : "Create User"}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

